<svelte:options
  customElement={{
    tag: 'dynamic-svelte-team-listing',
    props: {
      teamId: { type: 'String' },
      baseApiUrl: { type: 'String' },
      apiUrl: { type: 'String' },
      language: { type: 'String' },
      dictionary: { type: 'Object' },
      sports: { type: 'Array' },
      roles: { type: 'Array' },
      provinces: { type: 'Array' },
      communities: { type: 'Array' },
    },
    shadow: 'none',
  }}
/>

<script>
  import { SvelteURLSearchParams } from 'svelte/reactivity';
  import Pagination from '../pagination/pagination.svelte';

  let {
    teamId,
    language = 'en-US',
    dictionary,
    baseApiUrl = '',
    sports = [],
    roles = [],
    provinces = [],
    communities = [],
  } = $props();

  let apiUrl = $derived(baseApiUrl + '/api/teams/' + teamId);

  let urlParams = new SvelteURLSearchParams(window.location.search);
  const COUNT_PER_PAGE = 12;

  let filters = $state({
    role: Number(urlParams.get('role')) || '',
    territory: Number(urlParams.get('territory')) || '',
    sport: Number(urlParams.get('sport')) || '',
    community: Number(urlParams.get('community')) || '',
  });

  let currentPage = $derived(
    urlParams.has('page') ? Number(urlParams.get('page')) - 1 : 0
  );

  let finishedInitialSearch = $state(false);
  let listingItems = $state([]);
  let totalResults = $state(0);

  function onFiltersChange() {
    currentPage = 0;
    urlParams.set('page', 1);

    Object.entries(filters).forEach(([key, value]) => {
      urlParams.set(key, value);
    });

    updateUrl();
  }
  function onFiltersReset(e) {
    e.preventDefault();

    for (const key of Object.keys(filters)) {
      filters[key] = '';
    }
    onFiltersChange();
  }

  function getParams() {
    const params = new SvelteURLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      params.append(key, value);
    });

    params.append('page', currentPage);
    params.append('count', COUNT_PER_PAGE);
    params.append('lang', language); // Versive add current language

    return params;
  }

  async function fetchTeamData() {
    const params = getParams();
    let url = apiUrl + '?' + params.toString();

    try {
      // eslint-disable-next-line compat/compat
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      finishedInitialSearch = true;

      listingItems = data.teamMembers;
      totalResults = data.totalCount;
    } catch (error) {
      console.error('Fetch error:', error);
    }
  }

  function onPaginationClick(number) {
    currentPage = number;
    urlParams.set('page', number + 1);
    updateUrl();
  }

  function updateUrl() {
    const newPath = '?' + urlParams.toString();
    history.pushState({}, '', newPath);
    fetchTeamData();
  }

  fetchTeamData();
</script>

<!-- Team filters -->
<div class="team-filters">
  {#if sports.length > 0}
    <div class="team-filters__block">
      <label for="filter-sport" class="team-filters__label">{dictionary.Sport}</label>
      <div class="select-wrapper">
        <select
          id="filter-sport"
          data-team-filter
          class="team-filters__dropdown select soc_filter"
          name="sport"
          onchange={onFiltersChange}
          bind:value={filters.sport}
        >
          <option value="">{dictionary.ViewAll}</option>
          {#each sports as sport (sport.value)}
            <option value={sport.value}>{sport.name}</option>
          {/each}
        </select>
      </div>
    </div>
  {/if}
  {#if roles.length > 0}
    <div class="team-filters__block">
      <label for="filter-role" class="team-filters__label">{dictionary.Role}</label>
      <div class="select-wrapper">
        <select
          id="filter-role"
          data-team-filter
          class="team-filters__dropdown select soc_filter"
          name="role"
          onchange={onFiltersChange}
          bind:value={filters.role}
        >
          <option value="">{dictionary.ViewAll}</option>
          {#each roles as role (role.value)}
            <option value={role.value}>{role.name}</option>
          {/each}
        </select>
      </div>
    </div>
  {/if}

  {#if provinces.length > 0}
    <div class="team-filters__block">
      <label for="filter-territory" class="team-filters__label">
        {dictionary.Province}
      </label>
      <div class="select-wrapper">
        <select
          id="filter-territory"
          data-team-filter
          class="team-filters__dropdown select soc_filter"
          name="territory"
          onchange={onFiltersChange}
          bind:value={filters.territory}
        >
          <option value="">{dictionary.ViewAll}</option>
          {#each provinces as province (province.value)}
            <option value={province.value}>{province.name}</option>
          {/each}
        </select>
      </div>
    </div>
  {/if}

  {#if communities.length > 0}
    <div class="team-filters__block">
      <label for="filter-community" class="team-filters__label">
        {dictionary.Community}
      </label>
      <div class="select-wrapper">
        <select
          id="filter-community"
          data-team-filter
          class="team-filters__dropdown select soc_filter"
          name="community"
          onchange={onFiltersChange}
          bind:value={filters.community}
        >
          <option value="">{dictionary.ViewAll}</option>
          {#each communities as community (community.value)}
            <option value={community.value}>{community.name}</option>
          {/each}
        </select>
      </div>
    </div>
  {/if}
  <button
    class="team-filters__reset"
    data-team-filter-reset
    onclick={onFiltersReset}
  >
    {dictionary.ResetFilters}
  </button>
</div>

{#if finishedInitialSearch}
  {#if totalResults === 0}
    <div
      class="no-result search-listing__no-results displaynone"
      style="padding: 50px;min-height:150px;text-align:center;"
    >
      {dictionary.NoResults}
    </div>
  {:else}
    <ul class="team-listing" data-team-listing>
      {#each listingItems as member (member.link)}
        <li>
          <a href={member.link} class="team-card">
            <div class="team-card__image">
              <img src={member.photo} alt={member.name} />
            </div>
            <div class="team-card__content">
              <div class="team-card__name">{member.name}</div>
              <div class="team-card__territory">
                {#if member.community && member.territory}
                  {member.community}, {member.territory}
                {:else if member.community}
                  {member.community}
                {:else}
                  {member.territory}
                {/if}
              </div>
              <div class="team-card__info">
                <div class="team-card__role">{member.role}</div>
                |
                <div class="team-card__sport">{member.sport}</div>
              </div>
            </div>
          </a>
        </li>
      {/each}
    </ul>

    <!-- Pagination -->
    <Pagination
      totalCount={totalResults}
      countPerPage={COUNT_PER_PAGE}
      {currentPage}
      {dictionary}
      onClick={onPaginationClick}
    />
  {/if}
{/if}
